<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useParticipantAuth } from '@/composables/useParticipantAuth'
import { fetchCurrentUser, emailHasAccount } from '@/api/auth'
import { currentLocale } from '@/i18n'
import { FAVORITE_COUNTRIES, ALL_COUNTRIES } from '@/types/countries'
import type { User } from '@/types/auth'
import type { LegalDocumentKey } from '@/types/legal'

const props = defineProps<{
  raceRules: string | null
  termsAndConditions: string | null
}>()

const emit = defineEmits<{
  'registered': [user: User]
  'authenticated': [user: User]
  'logged-in': [user: User]
  'profile-updated': [user: User]
  'logged-out': []
  'open-legal-drawer': [doc: LegalDocumentKey, event: MouseEvent | KeyboardEvent]
}>()

const { t } = useI18n()
const {
  participantUser,
  isParticipantAuthenticated,
  registerParticipant,
  loginParticipant,
  updateParticipantProfile,
  logoutParticipant,
  setParticipantUser,
} = useParticipantAuth()

type Mode = 'register' | 'login' | 'authenticated'
const mode = ref<Mode>('register')
const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const suggestLogin = ref(false)

const acceptedTerms = ref(false)
const acceptanceError = ref<string | null>(null)

const hasRaceRules = computed(
  () => props.raceRules !== null && props.raceRules.trim() !== '',
)
const hasEventTerms = computed(
  () => props.termsAndConditions !== null && props.termsAndConditions.trim() !== '',
)

const acceptanceLabelKey = computed<string>(() => {
  if (hasRaceRules.value && hasEventTerms.value) {
    return 'registration.acceptance.label_all'
  }
  if (hasRaceRules.value) {
    return 'registration.acceptance.label_with_rules'
  }
  if (hasEventTerms.value) {
    return 'registration.acceptance.label_with_terms'
  }
  return 'registration.acceptance.label_startline_only'
})

watch(acceptedTerms, (val) => {
  if (val) acceptanceError.value = null
})

function validateAcceptance(): boolean {
  if (!acceptedTerms.value) {
    acceptanceError.value = t('registration.acceptance.error_required')
    return false
  }
  acceptanceError.value = null
  return true
}

function openStartlineTerms(): void {
  window.open('/terms/participants', '_blank', 'noopener,noreferrer')
}

// Reuses the existing password-reset flow (shared with the organizer app —
// see ParticipantLoginPage.vue for the same pattern). Opened in a new tab so
// the participant does not lose their in-progress registration wizard state;
// the reset itself is email-based and requires leaving the page regardless.
// The `context=participant` param routes "back to login" on that shared page
// to the participant login page instead of the organizer one (see #130).
function openForgotPassword(): void {
  window.open('/organizer/forgot-password?context=participant', '_blank', 'noopener,noreferrer')
}

const registerForm = reactive({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  birth_date: '',
  phone: '',
  gender: '',
  nationality: '',
})

const loginForm = reactive({
  email: '',
  password: '',
})

// Proactive existing-account detection (register mode only). Mirrors the
// debounce pattern in OrganizerOnboardingPage.vue: debounce the check, fail
// open on any error (422/429/network), and never block typing or submission.
const emailCheckTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const emailChecking = ref(false)
const emailExists = ref<boolean | null>(null)

const emailExistsActive = computed(
  () => mode.value === 'register' && emailExists.value === true,
)

function validateEmailFormat(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function onRegisterEmailInput(): void {
  if (emailCheckTimer.value) clearTimeout(emailCheckTimer.value)
  const email = registerForm.email.trim()
  if (!email || !validateEmailFormat(email)) {
    emailChecking.value = false
    emailExists.value = null
    return
  }
  emailCheckTimer.value = setTimeout(async () => {
    emailChecking.value = true
    try {
      emailExists.value = await emailHasAccount(email)
    } catch {
      // 422 (malformed), 429 (throttled), or a network error all resolve to
      // "unknown" — the check is advisory only and never surfaces an error.
      emailExists.value = null
    } finally {
      emailChecking.value = false
    }
  }, 300)
}

async function switchToLoginFromExistingEmail(): Promise<void> {
  loginForm.email = registerForm.email
  switchMode('login')
  await nextTick()
  document.getElementById('login-password')?.focus()
}

onUnmounted(() => {
  if (emailCheckTimer.value) clearTimeout(emailCheckTimer.value)
})

const profileForm = reactive({
  first_name: '',
  last_name: '',
  birth_date: '',
  phone: '',
  gender: '',
  nationality: '',
})

function populateProfileForm(user: User): void {
  profileForm.first_name = user.first_name
  profileForm.last_name = user.last_name
  profileForm.birth_date = user.birth_date ?? ''
  profileForm.phone = user.phone ?? ''
  profileForm.gender = user.gender ?? ''
  profileForm.nationality = user.nationality ?? ''
}

onMounted(() => {
  if (isParticipantAuthenticated.value && participantUser.value) {
    mode.value = 'authenticated'
    populateProfileForm(participantUser.value)
  }
})

watch(
  () => participantUser.value,
  (user) => {
    if (user) {
      mode.value = 'authenticated'
      populateProfileForm(user)
    }
  },
)

function isAxiosError(
  e: unknown,
): e is {
  response: {
    status: number
    data: { errors?: Record<string, string[]>; message?: string }
  }
} {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

function switchMode(to: Mode): void {
  mode.value = to
  errors.value = {}
  generalError.value = ''
  suggestLogin.value = false
  if (emailCheckTimer.value) clearTimeout(emailCheckTimer.value)
  emailChecking.value = false
  emailExists.value = null
}

function validateRegisterForm(): Record<string, string[]> {
  const errs: Record<string, string[]> = {}
  if (!registerForm.first_name) errs.first_name = [t('common.required_field')]
  if (!registerForm.last_name) errs.last_name = [t('common.required_field')]
  if (!registerForm.email) errs.email = [t('common.required_field')]
  if (!registerForm.password) errs.password = [t('common.required_field')]
  if (!registerForm.password_confirmation) {
    errs.password_confirmation = [t('common.required_field')]
  } else if (registerForm.password_confirmation !== registerForm.password) {
    errs.password_confirmation = [t('registration.account.password_confirmation_placeholder')]
  }
  if (!registerForm.birth_date) errs.birth_date = [t('common.required_field')]
  if (!registerForm.phone) errs.phone = [t('common.required_field')]
  if (!registerForm.gender) errs.gender = [t('common.required_field')]
  if (!registerForm.nationality) errs.nationality = [t('common.required_field')]
  return errs
}

function validateLoginForm(): Record<string, string[]> {
  const errs: Record<string, string[]> = {}
  if (!loginForm.email) errs.email = [t('common.required_field')]
  if (!loginForm.password) errs.password = [t('common.required_field')]
  return errs
}

function validateProfileForm(): Record<string, string[]> {
  const errs: Record<string, string[]> = {}
  if (!profileForm.first_name) errs.first_name = [t('common.required_field')]
  if (!profileForm.last_name) errs.last_name = [t('common.required_field')]
  if (!profileForm.birth_date) errs.birth_date = [t('common.required_field')]
  if (!profileForm.phone) errs.phone = [t('common.required_field')]
  if (!profileForm.gender) errs.gender = [t('common.required_field')]
  if (!profileForm.nationality) errs.nationality = [t('common.required_field')]
  return errs
}

async function handleSubmit(): Promise<void> {
  // State B: the create-account form/submit is hidden — an Enter keypress in
  // the email field should not silently attempt a duplicate-account submit.
  // Guide the participant to the login path instead.
  if (emailExistsActive.value) {
    await switchToLoginFromExistingEmail()
    return
  }

  if (!validateAcceptance()) return

  errors.value = {}
  generalError.value = ''
  suggestLogin.value = false

  if (mode.value === 'register') {
    const clientErrors = validateRegisterForm()
    if (Object.keys(clientErrors).length > 0) {
      errors.value = clientErrors
      return
    }

    loading.value = true
    try {
      const user = await registerParticipant({
        first_name: registerForm.first_name,
        last_name: registerForm.last_name,
        email: registerForm.email,
        password: registerForm.password,
        password_confirmation: registerForm.password_confirmation,
        birth_date: registerForm.birth_date,
        phone: registerForm.phone,
        gender: registerForm.gender,
        nationality: registerForm.nationality,
        preferred_language: currentLocale(),
      })
      emit('registered', user)
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        if (e.response.status === 422 && e.response.data.errors) {
          errors.value = e.response.data.errors
          if (e.response.data.errors.email) {
            suggestLogin.value = true
          }
        } else if (e.response.status === 403) {
          try {
            const user = await fetchCurrentUser()
            setParticipantUser(user)
            mode.value = 'authenticated'
            populateProfileForm(user)
            emit('authenticated', user)
            // An existing session was discovered mid-register — treat like a
            // login for the resume re-check (§6.5): this participant may
            // already have a registration for this race.
            emit('logged-in', user)
          } catch {
            generalError.value = t('common.error_generic')
          }
        } else {
          generalError.value = t('common.error_generic')
        }
      } else {
        generalError.value = t('common.error_generic')
      }
    } finally {
      loading.value = false
    }
  } else if (mode.value === 'login') {
    const clientErrors = validateLoginForm()
    if (Object.keys(clientErrors).length > 0) {
      errors.value = clientErrors
      return
    }

    loading.value = true
    try {
      const user = await loginParticipant({
        email: loginForm.email,
        password: loginForm.password,
      })
      mode.value = 'authenticated'
      populateProfileForm(user)
      emit('authenticated', user)
      // Distinct from a fresh registration: a returning participant may
      // already have a registration for this race (§6.5 resume re-check).
      emit('logged-in', user)
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        if (e.response.status === 422 && e.response.data.errors) {
          errors.value = e.response.data.errors
        } else if (e.response.status === 401) {
          generalError.value = t('registration.account.error_invalid_credentials')
        } else if (e.response.status === 403) {
          try {
            const user = await fetchCurrentUser()
            setParticipantUser(user)
            mode.value = 'authenticated'
            populateProfileForm(user)
            emit('authenticated', user)
            emit('logged-in', user)
          } catch {
            generalError.value = t('common.error_generic')
          }
        } else {
          generalError.value = t('common.error_generic')
        }
      } else {
        generalError.value = t('common.error_generic')
      }
    } finally {
      loading.value = false
    }
  } else {
    // authenticated mode: update profile
    const clientErrors = validateProfileForm()
    if (Object.keys(clientErrors).length > 0) {
      errors.value = clientErrors
      return
    }

    loading.value = true
    try {
      const user = await updateParticipantProfile({
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        birth_date: profileForm.birth_date,
        phone: profileForm.phone,
        gender: profileForm.gender,
        nationality: profileForm.nationality,
      })
      emit('profile-updated', user)
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        if (e.response.status === 422 && e.response.data.errors) {
          errors.value = e.response.data.errors
        } else {
          generalError.value = t('common.error_generic')
        }
      } else {
        generalError.value = t('common.error_generic')
      }
    } finally {
      loading.value = false
    }
  }
}

async function handleLogout(): Promise<void> {
  loading.value = true
  try {
    await logoutParticipant()
    mode.value = 'register'
    errors.value = {}
    generalError.value = ''
    acceptedTerms.value = false
    acceptanceError.value = null
    emit('logged-out')
  } catch {
    generalError.value = t('common.error_generic')
  } finally {
    loading.value = false
  }
}

function submitForm(): void {
  handleSubmit()
}

defineExpose({ submitForm, validateAcceptance })
</script>

<template>
  <div>
    <!-- Heading -->
    <h2 class="text-xl font-bold text-slate-900 mb-1">
      <template v-if="mode === 'register'">{{ t('registration.account.heading') }}</template>
      <template v-else-if="mode === 'login'">{{ t('registration.account.login_heading') }}</template>
      <template v-else>{{ t('registration.account.authenticated_heading') }}</template>
    </h2>

    <!-- Mode toggle (register/login only) -->
    <p v-if="mode !== 'authenticated'" class="text-sm text-slate-500 mb-5">
      <button
        type="button"
        class="text-emerald-600 hover:text-emerald-700 font-medium underline-offset-2 hover:underline"
        @click="switchMode(mode === 'register' ? 'login' : 'register')"
      >
        {{ mode === 'register' ? t('registration.account.toggle_to_login') : t('registration.account.toggle_to_register') }}
      </button>
    </p>

    <!-- Authenticated user info bar with logout -->
    <div
      v-if="mode === 'authenticated' && participantUser"
      class="mb-5 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3"
    >
      <p class="text-sm text-emerald-700">
        {{ t('registration.account.logged_in_as', { email: participantUser.email }) }}
      </p>
      <button
        type="button"
        :disabled="loading"
        class="text-sm font-medium text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline transition-colors"
        @click="handleLogout"
      >
        {{ t('registration.account.logout') }}
      </button>
    </div>

    <!-- General error -->
    <div
      v-if="generalError"
      class="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600"
    >
      {{ generalError }}
    </div>

    <!-- Email taken suggestion -->
    <div
      v-if="suggestLogin"
      class="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700"
    >
      {{ t('registration.account.error_email_taken') }}
      <button
        type="button"
        class="ml-1 font-semibold underline hover:no-underline"
        @click="switchMode('login')"
      >
        {{ t('registration.account.toggle_to_login') }}
      </button>
    </div>

    <form novalidate class="space-y-4" @submit.prevent="handleSubmit">
      <!-- REGISTER MODE -->
      <template v-if="mode === 'register'">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="acct-first-name" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.first_name_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="acct-first-name"
              v-model="registerForm.first_name"
              type="text"
              autocomplete="given-name"
              :placeholder="t('registration.account.first_name_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.first_name ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.first_name" class="mt-1 text-xs text-red-500">{{ errors.first_name[0] }}</p>
          </div>
          <div>
            <label for="acct-last-name" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.last_name_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="acct-last-name"
              v-model="registerForm.last_name"
              type="text"
              autocomplete="family-name"
              :placeholder="t('registration.account.last_name_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.last_name ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.last_name" class="mt-1 text-xs text-red-500">{{ errors.last_name[0] }}</p>
          </div>
        </div>

        <div>
          <label for="acct-email" class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.email_label') }} <span class="text-red-500">*</span>
          </label>
          <input
            id="acct-email"
            v-model="registerForm.email"
            type="email"
            autocomplete="email"
            :placeholder="t('registration.account.email_placeholder')"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.email ? 'border-red-400 bg-red-50' : (emailExists === true ? 'border-amber-400 bg-amber-50' : 'border-slate-300 bg-white focus:border-emerald-500')"
            @input="onRegisterEmailInput"
          />
          <p v-if="errors.email" class="mt-1 text-xs text-red-500">{{ errors.email[0] }}</p>
          <p v-else-if="emailChecking" class="mt-1 text-xs text-slate-400">{{ t('registration.account.email_checking') }}</p>
        </div>

        <!-- Existing-account notice (State B): steer toward login instead of a duplicate account -->
        <div
          v-if="emailExistsActive"
          class="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700"
        >
          {{ t('registration.account.email_exists_notice') }}
          <button
            type="button"
            class="ml-1 font-semibold underline hover:no-underline"
            @click="switchToLoginFromExistingEmail"
          >
            {{ t('registration.account.email_exists_login_cta') }}
          </button>
        </div>

        <template v-if="!emailExistsActive">
          <div>
            <label for="acct-password" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.password_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="acct-password"
              v-model="registerForm.password"
              type="password"
              autocomplete="new-password"
              :placeholder="t('registration.account.password_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.password ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.password" class="mt-1 text-xs text-red-500">{{ errors.password[0] }}</p>
          </div>

          <div>
            <label for="acct-password-confirm" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.password_confirmation_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="acct-password-confirm"
              v-model="registerForm.password_confirmation"
              type="password"
              autocomplete="new-password"
              :placeholder="t('registration.account.password_confirmation_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.password_confirmation ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.password_confirmation" class="mt-1 text-xs text-red-500">{{ errors.password_confirmation[0] }}</p>
          </div>

          <div>
            <label for="acct-birth-date" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.birth_date_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="acct-birth-date"
              v-model="registerForm.birth_date"
              type="date"
              autocomplete="bday"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.birth_date ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.birth_date" class="mt-1 text-xs text-red-500">{{ errors.birth_date[0] }}</p>
          </div>

          <div>
            <label for="acct-phone" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.phone_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="acct-phone"
              v-model="registerForm.phone"
              type="tel"
              autocomplete="tel"
              :placeholder="t('registration.account.phone_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.phone ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.phone" class="mt-1 text-xs text-red-500">{{ errors.phone[0] }}</p>
          </div>

          <div>
            <label for="acct-gender" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.gender_label') }} <span class="text-red-500">*</span>
            </label>
            <select
              id="acct-gender"
              v-model="registerForm.gender"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.gender ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            >
              <option value="" disabled>{{ t('registration.account.gender_placeholder') }}</option>
              <option value="male">{{ t('registration.account.gender_male') }}</option>
              <option value="female">{{ t('registration.account.gender_female') }}</option>
              <option value="non_binary">{{ t('registration.account.gender_non_binary') }}</option>
              <option value="prefer_not_to_say">{{ t('registration.account.gender_prefer_not_to_say') }}</option>
            </select>
            <p v-if="errors.gender" class="mt-1 text-xs text-red-500">{{ errors.gender[0] }}</p>
          </div>

          <div>
            <label for="acct-nationality" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.nationality_label') }} <span class="text-red-500">*</span>
            </label>
            <select
              id="acct-nationality"
              v-model="registerForm.nationality"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.nationality ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            >
              <option value="" disabled>{{ t('registration.account.nationality_placeholder') }}</option>
              <optgroup :label="t('registration.account.nationality_favorites')">
                <option v-for="country in FAVORITE_COUNTRIES" :key="'fav-' + country.code" :value="country.code">
                  {{ country.name }}
                </option>
              </optgroup>
              <optgroup :label="t('registration.account.nationality_all')">
                <option v-for="country in ALL_COUNTRIES" :key="country.code" :value="country.code">
                  {{ country.name }}
                </option>
              </optgroup>
            </select>
            <p v-if="errors.nationality" class="mt-1 text-xs text-red-500">{{ errors.nationality[0] }}</p>
          </div>
        </template>
      </template>

      <!-- LOGIN MODE -->
      <template v-else-if="mode === 'login'">
        <div>
          <label for="login-email" class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.email_label') }} <span class="text-red-500">*</span>
          </label>
          <input
            id="login-email"
            v-model="loginForm.email"
            type="email"
            autocomplete="email"
            :placeholder="t('registration.account.email_placeholder')"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.email ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
          />
          <p v-if="errors.email" class="mt-1 text-xs text-red-500">{{ errors.email[0] }}</p>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <label for="login-password" class="block text-sm font-medium text-slate-700">
              {{ t('registration.account.password_label') }} <span class="text-red-500">*</span>
            </label>
            <button
              type="button"
              class="text-xs text-emerald-600 hover:text-emerald-700 font-medium underline-offset-2 hover:underline"
              @click="openForgotPassword"
            >
              {{ t('registration.account.forgot_password_link') }}
            </button>
          </div>
          <input
            id="login-password"
            v-model="loginForm.password"
            type="password"
            autocomplete="current-password"
            :placeholder="t('registration.account.password_placeholder')"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.password ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
          />
          <p v-if="errors.password" class="mt-1 text-xs text-red-500">{{ errors.password[0] }}</p>
        </div>
      </template>

      <!-- AUTHENTICATED MODE (review/update profile) -->
      <template v-else>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="profile-first-name" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.first_name_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="profile-first-name"
              v-model="profileForm.first_name"
              type="text"
              autocomplete="given-name"
              :placeholder="t('registration.account.first_name_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.first_name ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.first_name" class="mt-1 text-xs text-red-500">{{ errors.first_name[0] }}</p>
          </div>
          <div>
            <label for="profile-last-name" class="block text-sm font-medium text-slate-700 mb-1">
              {{ t('registration.account.last_name_label') }} <span class="text-red-500">*</span>
            </label>
            <input
              id="profile-last-name"
              v-model="profileForm.last_name"
              type="text"
              autocomplete="family-name"
              :placeholder="t('registration.account.last_name_placeholder')"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.last_name ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
            />
            <p v-if="errors.last_name" class="mt-1 text-xs text-red-500">{{ errors.last_name[0] }}</p>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.email_label') }}
          </label>
          <input
            type="email"
            disabled
            :value="participantUser?.email ?? ''"
            class="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-slate-500 text-sm cursor-not-allowed"
          />
        </div>

        <div>
          <label for="profile-birth-date" class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.birth_date_label') }} <span class="text-red-500">*</span>
          </label>
          <input
            id="profile-birth-date"
            v-model="profileForm.birth_date"
            type="date"
            autocomplete="bday"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.birth_date ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
          />
          <p v-if="errors.birth_date" class="mt-1 text-xs text-red-500">{{ errors.birth_date[0] }}</p>
        </div>

        <div>
          <label for="profile-phone" class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.phone_label') }} <span class="text-red-500">*</span>
          </label>
          <input
            id="profile-phone"
            v-model="profileForm.phone"
            type="tel"
            autocomplete="tel"
            :placeholder="t('registration.account.phone_placeholder')"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.phone ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
          />
          <p v-if="errors.phone" class="mt-1 text-xs text-red-500">{{ errors.phone[0] }}</p>
        </div>

        <div>
          <label for="profile-gender" class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.gender_label') }} <span class="text-red-500">*</span>
          </label>
          <select
            id="profile-gender"
            v-model="profileForm.gender"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.gender ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
          >
            <option value="" disabled>{{ t('registration.account.gender_placeholder') }}</option>
            <option value="male">{{ t('registration.account.gender_male') }}</option>
            <option value="female">{{ t('registration.account.gender_female') }}</option>
            <option value="non_binary">{{ t('registration.account.gender_non_binary') }}</option>
            <option value="prefer_not_to_say">{{ t('registration.account.gender_prefer_not_to_say') }}</option>
          </select>
          <p v-if="errors.gender" class="mt-1 text-xs text-red-500">{{ errors.gender[0] }}</p>
        </div>

        <div>
          <label for="profile-nationality" class="block text-sm font-medium text-slate-700 mb-1">
            {{ t('registration.account.nationality_label') }} <span class="text-red-500">*</span>
          </label>
          <select
            id="profile-nationality"
            v-model="profileForm.nationality"
            class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.nationality ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
          >
            <option value="" disabled>{{ t('registration.account.nationality_placeholder') }}</option>
            <optgroup :label="t('registration.account.nationality_favorites')">
              <option v-for="country in FAVORITE_COUNTRIES" :key="'fav-' + country.code" :value="country.code">
                {{ country.name }}
              </option>
            </optgroup>
            <optgroup :label="t('registration.account.nationality_all')">
              <option v-for="country in ALL_COUNTRIES" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </optgroup>
          </select>
          <p v-if="errors.nationality" class="mt-1 text-xs text-red-500">{{ errors.nationality[0] }}</p>
        </div>
      </template>

      <!-- Mandatory legal acceptance — visible in all modes except the de-emphasised State B (email exists) -->
      <div v-if="!emailExistsActive" class="pt-2">
        <label class="flex items-start gap-3 cursor-pointer">
          <input
            id="acct-accept-terms"
            v-model="acceptedTerms"
            type="checkbox"
            class="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
            :class="{ 'border-red-400': acceptanceError }"
            :aria-invalid="acceptanceError !== null"
            aria-describedby="acct-accept-terms-error"
          />
          <span class="text-sm text-slate-700">
            <i18n-t :keypath="acceptanceLabelKey" tag="span" scope="global">
              <template #startline>
                <button
                  type="button"
                  class="text-emerald-600 hover:text-emerald-700 underline-offset-2 underline font-medium"
                  @click.stop.prevent="emit('open-legal-drawer', 'startline_terms', $event as MouseEvent)"
                >
                  {{ t('registration.acceptance.link_startline') }}
                </button>
              </template>
              <template v-if="hasRaceRules" #rules>
                <button
                  type="button"
                  class="text-emerald-600 hover:text-emerald-700 underline-offset-2 underline font-medium"
                  @click.stop.prevent="emit('open-legal-drawer', 'race_rules', $event as MouseEvent)"
                >
                  {{ t('registration.acceptance.link_race_rules') }}
                </button>
              </template>
              <template v-if="hasEventTerms" #terms>
                <button
                  type="button"
                  class="text-emerald-600 hover:text-emerald-700 underline-offset-2 underline font-medium"
                  @click.stop.prevent="emit('open-legal-drawer', 'terms_and_conditions', $event as MouseEvent)"
                >
                  {{ t('registration.acceptance.link_event_terms') }}
                </button>
              </template>
            </i18n-t>
          </span>
        </label>
        <p
          v-if="acceptanceError"
          id="acct-accept-terms-error"
          class="mt-1 text-xs text-red-500"
        >
          {{ acceptanceError }}
        </p>
      </div>

      <!-- Submit button (register and login modes only -- authenticated mode uses the wizard nav buttons; de-emphasised/hidden for State B) -->
      <button
        v-if="mode !== 'authenticated' && !emailExistsActive"
        type="submit"
        :disabled="loading"
        class="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
      >
        <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {{ loading ? t('common.loading') : (mode === 'register' ? t('registration.account.submit_register') : t('registration.account.submit_login')) }}
      </button>
    </form>
  </div>
</template>
